import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/angular-table';
import { paramToNumber } from 'ngxtension/linked-query-param';

type StringifyReturnType = string | number | boolean | null | undefined;
type ParseFn<T> = (value: string | null) => T;
type StringifyFn<T> = (value: T | null | undefined) => StringifyReturnType;

interface QueryParamTransformer<T> {
    parse: ParseFn<T>;
    stringify: StringifyFn<T>;
}

const paramToJSON = <T>(defaultValue: T): ParseFn<T> => (value: string | null) => value ? JSON.parse(value) as T : defaultValue;
const JSONToParam = <T>(value: T | null | undefined): StringifyReturnType => value == null ? value as (null | undefined) : JSON.stringify(value);
const numberToParam = (value: number | null | undefined): StringifyReturnType => value == null ? value as (null | undefined) : value.toString();

export const pageIndexQueryParam = (defaultValue: number = 0): QueryParamTransformer<number> => ({
    parse: paramToNumber({ defaultValue }),
    stringify: numberToParam
});

export const pageSizeQueryParam = (defaultValue: number = 0): QueryParamTransformer<number> => ({
    parse: paramToNumber({ defaultValue }),
    stringify: numberToParam
});

export const sortingQueryParams = (defaultValue: SortingState = []): QueryParamTransformer<SortingState> => ({
    parse: paramToJSON<SortingState>(defaultValue),
    stringify: JSONToParam
});

export const columnFiltersQueryParams = (defaultValue: ColumnFiltersState = []): QueryParamTransformer<ColumnFiltersState> => ({
    parse: paramToJSON<ColumnFiltersState>(defaultValue),
    stringify: JSONToParam
});

export interface TableSate {
    search: string | null;
    pagination: PaginationState;
    sorting: SortingState;
    columnFilters?: ColumnFiltersState;
}

export const tableQueryParamsTransformer = (defaultValues?: Partial<TableSate>) => {
    const pageIndexTransformer = pageIndexQueryParam(defaultValues?.pagination?.pageIndex);
    const pageSizeTransformer = pageSizeQueryParam(defaultValues?.pagination?.pageSize);
    const sortingTransformer = sortingQueryParams(defaultValues?.sorting);
    const columnFiltersTransformer = columnFiltersQueryParams(defaultValues?.columnFilters);

    return {
        parse: (queryParams?: Record<string, string | null>) => ({
            search: queryParams?.['search'] ?? null,
            pageIndex: pageIndexTransformer.parse(queryParams?.['pageIndex'] ?? null),
            pageSize: pageSizeTransformer.parse(queryParams?.['pageSize'] ?? null),
            sort: sortingTransformer.parse(queryParams?.['sort'] ?? null),
            columnFilters: columnFiltersTransformer.parse(queryParams?.['columnFilters'] ?? null)
        }),
        stringify: (state: Partial<TableSate>) => ({
            search: state?.search ?? null,
            pageIndex: pageIndexTransformer.stringify(state?.pagination?.pageIndex),
            pageSize: pageSizeTransformer.stringify(state?.pagination?.pageSize),
            sort: sortingTransformer.stringify(state?.sorting),
            columnFilters: columnFiltersTransformer.stringify(state?.columnFilters)
        })
    };
};
